/**
 * useByocDashboard - single integration point for every query the BYOC admin
 * dashboard depends on.
 *
 * The page stays pure composition (same split as `useEmployeeDashboard` and
 * `usePlatformDashboard`): everything network-shaped lives here, and so does the
 * bucketing, so the charts receive finished series and stay presentational.
 *
 * The one query not here is the Top categories one, which lives in its component
 * via `useSpendingData`.
 */
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "utils/dayjs";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import CardType from "enums/CardType";
import CardStatusType from "enums/CardStatusType";
import {
  GET_ACCOUNT_CARD_COUNT,
  GET_COMPLIANCE_METRICS,
} from "graphql/byocDashboard";
import { GET_TRANSACTION_AMOUNTS, GET_TRANSACTIONS } from "graphql/transaction";
import useGetAccounts, { ExternalAccount } from "hooks/useGetAccounts";
import { ComplianceStatusDisplay } from "components/common/RenderCompliance";
import {
  DailyBucket,
  PeriodComparison,
  SpendTransaction,
  WeeklyBucket,
  toAccountTotals,
  toDailyBuckets,
  toPeriodComparison,
  toWeeklyComparison,
} from "utils/spendBuckets";

/** The headline window, and the span of the area chart. */
const SPEND_WINDOW_DAYS = 30;

/** Weekly buckets per block in the spend comparison. */
const COMPARISON_WEEKS = 4;

/** Rows in the recent-transactions sub-list. */
const RECENT_TRANSACTION_COUNT = 10;

/**
 * Statuses an external card row holds while it moves money, and so the only
 * ones the spend charts sum. Credits among them are netted by sign in
 * `spendBuckets`.
 */
const SPEND_STATUSES = [
  TransactionStatus.PENDING,
  TransactionStatus.COMPLETE,
  TransactionStatus.REFUNDED,
  TransactionStatus.REVERSED,
];

/** What the recent list shows: spend plus the attempts that moved no money. */
const ACTIVITY_STATUSES = [
  ...SPEND_STATUSES,
  TransactionStatus.DECLINED,
  TransactionStatus.INSUFFICIENT_FUNDS,
  TransactionStatus.PENDING_PAYMENT,
  TransactionStatus.PROCESSING_PAYMENT,
  TransactionStatus.PAYMENT_FAIL,
];

const SPEND_TYPES = [
  TransactionsType.AUTHORIZATION,
  TransactionsType.CAPTURE,
  TransactionsType.MILEAGE,
  TransactionsType.CASH,
  TransactionsType.REFUND,
];

/**
 * This dashboard is external cards only, and `transactions` returns every card
 * type unless told otherwise.
 */
const EXTERNAL_CARD_TYPES = [CardType.EXTERNAL];

// -- Raw query shapes ---------------------------------------------------------

interface SpendTransactionsResponse {
  transactions?: {
    edges?: Array<{ node?: SpendTransaction | null } | null> | null;
  } | null;
}

interface CardCountResponse {
  cards?: {
    edges?: Array<{ node?: { id: string } | null } | null> | null;
  } | null;
}

interface ComplianceMetricsResponse {
  complianceMetrics?: ComplianceMetrics | null;
}

interface RecentTransactionsResponse {
  transactions?: {
    edges?: Array<{ node?: ByocTransaction | null } | null> | null;
  } | null;
}

// -- View model ---------------------------------------------------------------

export interface ComplianceUserTotal {
  userId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  count: number;
  totalAmount: number;
}

export interface ComplianceMetrics {
  count: number;
  totalAmount: number;
  oldestTransactionDate?: number | null;
  currency?: string | null;
  byUser: ComplianceUserTotal[];
}

interface ViolationDetail {
  violationStatus?: string | null;
  isExempt?: boolean | null;
}

/** The merchant fields `GET_TRANSACTIONS` actually selects. */
export interface TransactionMerchant {
  merchantId?: string | null;
  name?: string | null;
  url?: string | null;
  logo?: string | null;
  cachedLogo?: string | null;
}

export interface ByocTransaction {
  id: string;
  amount: number;
  currency?: string | null;
  transactionDate?: number | null;
  merchant?: string | null;
  merchantData?: TransactionMerchant | null;
  accountId?: string | null;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  policyStatus?: {
    receipt?: ViolationDetail | null;
    allocation?: ViolationDetail | null;
  } | null;
  policyStatusDisplay?: ComplianceStatusDisplay | null;
}

/** One row in the Connected accounts panel. */
export interface ConnectedAccount {
  id: string;
  name: string;
  url?: string | null;
  status: ExternalAccount["status"];
  /** Epoch millis of the last sync, null if it has never synced. */
  refreshedAt?: number | null;
  cardCount: number;
  /** Spend on this account across the headline window. */
  spend: number;
}

export interface SpendSummary {
  /** Daily series across the headline window, oldest first. */
  daily: DailyBucket[];
  comparison: PeriodComparison;
  windowDays: number;
  currency: string;
}

export interface ByocDashboardLoadingStates {
  spend: boolean;
  compliance: boolean;
  transactions: boolean;
  accounts: boolean;
}

export interface ByocDashboardErrorStates {
  spend: boolean;
  compliance: boolean;
  transactions: boolean;
  accounts: boolean;
}

export interface ByocDashboardData {
  spend: SpendSummary;
  weekly: WeeklyBucket[];
  missingReceipts: ComplianceMetrics;
  unallocated: ComplianceMetrics;
  accounts: ConnectedAccount[];
  transactions: ByocTransaction[];
  isLoading: boolean;
  loading: ByocDashboardLoadingStates;
  errors: ByocDashboardErrorStates;
  /** True when everything failed and there is nothing cached to render. */
  hasFatalError: boolean;
  refetch: () => void;
}

const EMPTY_COMPLIANCE: ComplianceMetrics = {
  count: 0,
  totalAmount: 0,
  oldestTransactionDate: null,
  currency: "usd",
  byUser: [],
};

// -- Per-account card counts --------------------------------------------------

/**
 * Card count for one linked account.
 *
 * A hook per account rather than one batched query because `cards` takes a
 * single `accountId`. Accounts are rendered from a stable list, so the hook
 * order is stable too; `skip` covers the first render before accounts arrive.
 */
function useAccountCardCount(accountId?: string) {
  const { data } = useQuery<CardCountResponse>(GET_ACCOUNT_CARD_COUNT, {
    variables: {
      accountId,
      cardType: [CardType.EXTERNAL],
      cardStatus: [CardStatusType.ACTIVE],
    },
    skip: !accountId,
    fetchPolicy: "cache-first",
  });
  return data?.cards?.edges?.length ?? 0;
}

// -- Hook ---------------------------------------------------------------------

export function useByocDashboard(): ByocDashboardData {
  // One 90-day pull feeds the area chart, its prior-period trend, the eight
  // weekly buckets, and per-account spend. `DatePeriod` has no "days 31 to 60"
  // member, so the earlier windows are sliced from this rather than fetched.
  const {
    data: spendData,
    loading: spendLoading,
    error: spendError,
    refetch: refetchSpend,
  } = useQuery<SpendTransactionsResponse>(GET_TRANSACTION_AMOUNTS, {
    variables: {
      date: "last_90_days",
      type: SPEND_TYPES,
      transactionStatus: SPEND_STATUSES,
      cardType: EXTERNAL_CARD_TYPES,
    },
    fetchPolicy: "cache-and-network",
  });

  // Two calls, not one: the underlying conditions are ORed, so a combined
  // request would return the union with no way to split it back apart.
  const {
    data: receiptsData,
    loading: receiptsLoading,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useQuery<ComplianceMetricsResponse>(GET_COMPLIANCE_METRICS, {
    variables: { compliance: "MISSING_RECEIPT" },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: allocationsData,
    loading: allocationsLoading,
    error: allocationsError,
    refetch: refetchAllocations,
  } = useQuery<ComplianceMetricsResponse>(GET_COMPLIANCE_METRICS, {
    variables: { compliance: "MISSING_COST_ALLOCATION" },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: txData,
    loading: txLoading,
    error: txError,
    refetch: refetchTransactions,
  } = useQuery<RecentTransactionsResponse>(GET_TRANSACTIONS, {
    variables: {
      first: RECENT_TRANSACTION_COUNT,
      transactionStatus: ACTIVITY_STATUSES,
      type: SPEND_TYPES,
      cardType: EXTERNAL_CARD_TYPES,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    activeAccounts,
    externalAccounts,
    loading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useGetAccounts();

  const spendTransactions: SpendTransaction[] = useMemo(() => {
    const edges = spendData?.transactions?.edges ?? [];
    return edges.reduce<SpendTransaction[]>((acc, edge) => {
      const node = edge?.node;
      if (node && typeof node.id === "string") acc.push(node);
      return acc;
    }, []);
  }, [spendData]);

  // Every series is derived in one pass from a single `now`, so the area chart,
  // the trend it is compared against, the weekly buckets and the per-account
  // totals all cut their windows at the same instant. Splitting these into
  // separate memos, each calling `dayjs()`, let a render that straddles
  // midnight put the panels a day apart.
  const { spend, weekly, accountSpend } = useMemo(() => {
    const now = dayjs();
    return {
      spend: {
        daily: toDailyBuckets(spendTransactions, SPEND_WINDOW_DAYS, now),
        comparison: toPeriodComparison(
          spendTransactions,
          SPEND_WINDOW_DAYS,
          now,
        ),
        windowDays: SPEND_WINDOW_DAYS,
        currency: spendTransactions[0]?.currency ?? "usd",
      } as SpendSummary,
      weekly: toWeeklyComparison(spendTransactions, COMPARISON_WEEKS, now),
      accountSpend: toAccountTotals(spendTransactions, SPEND_WINDOW_DAYS, now),
    };
  }, [spendTransactions]);

  // Hooks cannot be called in a loop over a dynamic list, so the panel is
  // capped at the accounts a company realistically links. Anything past the cap
  // still renders, just without a card count.
  const cardCount0 = useAccountCardCount(activeAccounts[0]?.id);
  const cardCount1 = useAccountCardCount(activeAccounts[1]?.id);
  const cardCount2 = useAccountCardCount(activeAccounts[2]?.id);
  const cardCount3 = useAccountCardCount(activeAccounts[3]?.id);
  const cardCounts = useMemo(
    () => [cardCount0, cardCount1, cardCount2, cardCount3],
    [cardCount0, cardCount1, cardCount2, cardCount3],
  );

  const accounts: ConnectedAccount[] = useMemo(
    () =>
      activeAccounts.map((account: ExternalAccount, i: number) => ({
        id: account.id,
        name: account.name,
        url: account.url,
        status: account.status,
        refreshedAt: account.refreshedAt,
        cardCount: cardCounts[i] ?? 0,
        spend: accountSpend[account.id] ?? 0,
      })),
    [activeAccounts, cardCounts, accountSpend],
  );

  const transactions: ByocTransaction[] = useMemo(() => {
    const edges = txData?.transactions?.edges ?? [];
    return edges.reduce<ByocTransaction[]>((acc, edge) => {
      const node = edge?.node;
      if (node && typeof node.id === "string") acc.push(node);
      return acc;
    }, []);
  }, [txData]);

  const loading: ByocDashboardLoadingStates = {
    spend: spendLoading,
    compliance: receiptsLoading || allocationsLoading,
    transactions: txLoading,
    accounts: accountsLoading,
  };

  const errors: ByocDashboardErrorStates = {
    spend: Boolean(spendError),
    compliance: Boolean(receiptsError || allocationsError),
    transactions: Boolean(txError),
    accounts: Boolean(accountsError),
  };

  // Only a whole-page failure earns the page-level error state. One failed
  // query leaves its own panel empty while the rest still renders, and
  // `cache-and-network` means a transient refetch error still has data.
  // Cached compliance results count as something to render, the same as cached
  // spend or transactions: a company whose other queries failed can still be
  // shown its receipt and allocation backlog rather than a blank error page.
  const hasFatalError =
    errors.spend &&
    errors.compliance &&
    errors.transactions &&
    errors.accounts &&
    !spendData &&
    !txData &&
    !receiptsData &&
    !allocationsData &&
    externalAccounts.length === 0;

  const refetch = () => {
    refetchSpend();
    refetchReceipts();
    refetchAllocations();
    refetchTransactions();
    refetchAccounts();
  };

  return {
    spend,
    weekly,
    missingReceipts: receiptsData?.complianceMetrics ?? EMPTY_COMPLIANCE,
    unallocated: allocationsData?.complianceMetrics ?? EMPTY_COMPLIANCE,
    accounts,
    transactions,
    isLoading:
      loading.spend ||
      loading.compliance ||
      loading.transactions ||
      loading.accounts,
    loading,
    errors,
    hasFatalError,
    refetch,
  };
}

export default useByocDashboard;
